import React, { useState } from 'react';
import s from './s.module.less';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { message } from 'antd';
import dayjs from 'utils/dayjs';
import copy from 'copy-to-clipboard';
import useTimezone from 'hooks/useTimezone';

type Props = {
    visit: TServerCalendarAppointmentEventDetail;
    onReschedule: () => void;
    header?: React.ReactElement;
    showClientInfo?: boolean;
    patientName?: string;
}

const VisitCard: React.FC<Props> = ({ visit, onReschedule, header, showClientInfo = false, patientName }) => {
    const [, myTimezone] = useTimezone();
    const formatDate = (dateStr: string, format?: string) => {
        return dayjs(dateStr).tz(myTimezone?.value as string).format(format || 'h:mm a');
    };

    const handleCopyLink = () => {
        if (visit.externalVideoLink) {
            copy(visit.externalVideoLink);
            message.success('Copied to the clipboard');
        }
    };

    // 获取Klarity Meet链接
    const getMeetLink = () => {
        return visit.externalVideoLink || 'https://provider.klaritymeetlink.com';
    };

    return (
        <>
            <div className={s.visitCard}>
                {header}
                <div className={s.cardContent}>
                    <div className={s.content}>
                        <h3 className={s.title}>{visit.appointmentName}</h3>
                        {showClientInfo && (
                            <div className={s.infoRow}>
                                <div className={s.infoItem}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M12 5.25C12 6.90685 10.6569 8.25 9 8.25C7.34315 8.25 6 6.90685 6 5.25C6 3.59315 7.34315 2.25 9 2.25C10.6569 2.25 12 3.59315 12 5.25Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M9 10.5C6.10051 10.5 3.75 12.8505 3.75 15.75H14.25C14.25 12.8505 11.8995 10.5 9 10.5Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <span
                                        className={s.clientName}
                                        style={{ color: '#0E62F4', cursor: 'pointer' }}
                                        onClick={() => {
                                            window.open(`/dashboard/prospects-detail/${visit.clientId}`, '_blank');
                                        }}>{patientName || visit.appointmentName.split('-')[0]}</span>
                                    <span style={{ cursor: 'pointer' }} onClick={() => {
                                        window.open(`/dashboard/inbox?clientId=${visit.clientId}`, '_blank');
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" >
                                            <path d="M5.33333 8.00033H5.34M8 8.00033H8.00667M10.6667 8.00033H10.6733M14 8.00033C14 10.9458 11.3137 13.3337 8 13.3337C6.97382 13.3337 6.00781 13.1047 5.16311 12.701L2 13.3337L2.92999 10.8537C2.34104 10.0286 2 9.04984 2 8.00033C2 5.05481 4.68629 2.66699 8 2.66699C11.3137 2.66699 14 5.05481 14 8.00033Z" stroke="#0E62F4" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" className={s.icon}>
                                    <path d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span>{formatDate(visit.startAt, 'dddd, MMMM D, YYYY')}</span>
                            </div>
                            <div className={s.infoItem}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={s.icon}>
                                    <path d="M8 5.33333V8L10 10M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span>{formatDate(visit.startAt, 'h:mm')} - {formatDate(visit.endAt)} ({myTimezone?.value}) | {visit.duration} mins</span>
                            </div>
                        </div>

                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <div className={s.klarityIcon}>K</div>
                                <div className={s.meetLinkContent}>
                                    <span>Klarity Meet: </span>
                                    <a href={getMeetLink()} target="_blank" rel="noopener noreferrer" className={s.meetLink}>
                                        {getMeetLink()}
                                    </a>
                                </div>
                                <div className={s.copyBtn} onClick={handleCopyLink}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={s.icon}>
                                        <g clip-path="url(#clip0_493_36878)">
                                            <path d="M5.33203 2.66634V10.6663C5.33203 11.02 5.47251 11.3591 5.72256 11.6092C5.9726 11.8592 6.31174 11.9997 6.66536 11.9997H11.9987C12.3523 11.9997 12.6915 11.8592 12.9415 11.6092C13.1916 11.3591 13.332 11.02 13.332 10.6663V4.82767C13.332 4.65005 13.2965 4.47422 13.2276 4.3105C13.1587 4.14679 13.0577 3.9985 12.9307 3.87434L10.7207 1.71301C10.4716 1.46945 10.1371 1.33305 9.7887 1.33301H6.66536C6.31174 1.33301 5.9726 1.47348 5.72256 1.72353C5.47251 1.97358 5.33203 2.31272 5.33203 2.66634Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M10.666 11.9993V13.3327C10.666 13.6863 10.5255 14.0254 10.2755 14.2755C10.0254 14.5255 9.6863 14.666 9.33268 14.666H3.99935C3.64573 14.666 3.30659 14.5255 3.05654 14.2755C2.80649 14.0254 2.66602 13.6863 2.66602 13.3327V5.99935C2.66602 5.64573 2.80649 5.30659 3.05654 5.05654C3.30659 4.80649 3.64573 4.66602 3.99935 4.66602H5.33268" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_493_36878">
                                                <rect width="16" height="16" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={s.icon}>
                                    <path d="M14 8.83663C12.1471 9.58665 10.1218 9.99966 8 9.99966C5.8782 9.99966 3.85286 9.58665 2 8.83663M10.6667 3.99967V2.66634C10.6667 1.92996 10.0697 1.33301 9.33333 1.33301H6.66667C5.93029 1.33301 5.33333 1.92996 5.33333 2.66634V3.99967M8 7.99967H8.00667M3.33333 13.333H12.6667C13.403 13.333 14 12.7361 14 11.9997V5.33301C14 4.59663 13.403 3.99967 12.6667 3.99967H3.33333C2.59695 3.99967 2 4.59663 2 5.33301V11.9997C2 12.7361 2.59695 13.333 3.33333 13.333Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span>{visit.serviceType}</span>
                            </div>
                            {visit.serviceSpecialty && <div className={s.infoItem}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={s.icon}>
                                    <path d="M12.6667 14V3.33333C12.6667 2.59695 12.0697 2 11.3333 2H4.66667C3.93029 2 3.33333 2.59695 3.33333 3.33333V14M12.6667 14L14 14M12.6667 14H9.33333M3.33333 14L2 14M3.33333 14H6.66667M6 4.66665H6.66667M6 7.33332H6.66667M9.33333 4.66665H10M9.33333 7.33332H10M6.66667 14V10.6667C6.66667 10.2985 6.96514 9.99999 7.33333 9.99999H8.66667C9.03486 9.99999 9.33333 10.2985 9.33333 10.6667V14M6.66667 14H9.33333" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span>{visit.serviceSpecialty}</span>
                            </div>}
                        </div>
                    </div>

                    <div className={s.actions}>
                        <div className={s.changeTimeBtn} onClick={onReschedule}>Change time</div>
                        <span className={s.statusBadge}>Scheduled</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VisitCard;